/**
 * Simple Business Intelligence state management
 * 
 * Manages the state for business intelligence data including:
 * - Company profile data
 * - Competitor profiles
 * - Form state and validation
 */

import { useState, useCallback } from 'react';
import {
  BusinessIntelligence,
  BusinessProfile,
  BusinessIntelligenceFormData,
  Attraction,
  MembershipPlan,
  BirthdayPackage,
  MarketingChannel,
  FoodAndBeverage
} from '../types/business-intelligence';
import { businessIntelligenceService } from '../services/business-intelligence';

interface BusinessIntelligenceState {
  // Core data
  businessIntelligence: BusinessIntelligence | null;
  isLoading: boolean;
  error: string | null;
  
  // Form state
  formData: BusinessIntelligenceFormData;
  isFormDirty: boolean;
  isSaving: boolean;
  saveError: string | null;
  
  // UI state
  activeTab: string;
  expandedSections: Record<string, boolean>;
  
  // Google Places integration
  isDiscovering: boolean;
  discoveryResults: any[];
  discoveryError: string | null;
}

const defaultFormData: BusinessIntelligenceFormData = {
  companyProfile: {
    locationName: '',
    address: '',
    isExistingLocation: false,
    openingDate: undefined,
    website: undefined,
    isFranchise: false,
    franchiseName: undefined,
  },
  attractions: [],
  membershipPlans: [],
  birthdayPackages: [],
  foodAndBeverage: undefined,
  operationalRatings: {
    cleanliness: undefined,
    staffEngagement: undefined,
    safetyRating: undefined,
  },
  onlinePresence: {
    googleRating: undefined,
    totalReviews: undefined,
    reviewNotes: undefined,
  },
  marketingChannels: [],
};

const initialState: BusinessIntelligenceState = {
  businessIntelligence: null,
  isLoading: false,
  error: null,
  
  formData: defaultFormData,
  isFormDirty: false,
  isSaving: false,
  saveError: null,
  
  activeTab: 'overview',
  expandedSections: {
    basicInfo: true,
    attractions: true,
    pricing: false,
    operations: false,
    marketing: false,
  },
  
  isDiscovering: false,
  discoveryResults: [],
  discoveryError: null,
};

// Create a simple state store hook
export function useBusinessIntelligenceStore() {
  const [state, setState] = useState<BusinessIntelligenceState>(initialState);

  // Basic setters
  const setBusinessIntelligence = useCallback((bi: BusinessIntelligence | null) => {
    setState(prev => ({
      ...prev,
      businessIntelligence: bi,
      formData: bi ? convertToFormData(bi) : defaultFormData,
      isFormDirty: false
    }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  // Form actions
  const updateFormData = useCallback((updates: Partial<BusinessIntelligenceFormData>) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, ...updates },
      isFormDirty: true
    }));
  }, []);

  const resetFormData = useCallback(() => {
    setState(prev => ({
      ...prev,
      formData: defaultFormData,
      isFormDirty: false
    }));
  }, []);

  const setFormDirty = useCallback((dirty: boolean) => {
    setState(prev => ({ ...prev, isFormDirty: dirty }));
  }, []);

  // Company profile actions
  const updateCompanyProfile = useCallback((updates: Partial<BusinessIntelligenceFormData['companyProfile']>) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        companyProfile: { ...prev.formData.companyProfile, ...updates }
      },
      isFormDirty: true
    }));
  }, []);

  const addAttraction = useCallback((attraction: Attraction) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        attractions: [...prev.formData.attractions, attraction]
      },
      isFormDirty: true
    }));
  }, []);

  const updateAttraction = useCallback((index: number, attraction: Attraction) => {
    setState(prev => {
      const newAttractions = [...prev.formData.attractions];
      if (index >= 0 && index < newAttractions.length) {
        newAttractions[index] = attraction;
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          attractions: newAttractions
        },
        isFormDirty: true
      };
    });
  }, []);

  const removeAttraction = useCallback((index: number) => {
    setState(prev => {
      const newAttractions = [...prev.formData.attractions];
      if (index >= 0 && index < newAttractions.length) {
        newAttractions.splice(index, 1);
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          attractions: newAttractions
        },
        isFormDirty: true
      };
    });
  }, []);

  const addMembershipPlan = useCallback((plan: MembershipPlan) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        membershipPlans: [...prev.formData.membershipPlans, plan]
      },
      isFormDirty: true
    }));
  }, []);

  const updateMembershipPlan = useCallback((index: number, plan: MembershipPlan) => {
    setState(prev => {
      const newPlans = [...prev.formData.membershipPlans];
      if (index >= 0 && index < newPlans.length) {
        newPlans[index] = plan;
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          membershipPlans: newPlans
        },
        isFormDirty: true
      };
    });
  }, []);

  const removeMembershipPlan = useCallback((index: number) => {
    setState(prev => {
      const newPlans = [...prev.formData.membershipPlans];
      if (index >= 0 && index < newPlans.length) {
        newPlans.splice(index, 1);
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          membershipPlans: newPlans
        },
        isFormDirty: true
      };
    });
  }, []);

  const addBirthdayPackage = useCallback((birthdayPackage: BirthdayPackage) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        birthdayPackages: [...prev.formData.birthdayPackages, birthdayPackage]
      },
      isFormDirty: true
    }));
  }, []);

  const updateBirthdayPackage = useCallback((index: number, birthdayPackage: BirthdayPackage) => {
    setState(prev => {
      const newPackages = [...prev.formData.birthdayPackages];
      if (index >= 0 && index < newPackages.length) {
        newPackages[index] = birthdayPackage;
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          birthdayPackages: newPackages
        },
        isFormDirty: true
      };
    });
  }, []);

  const removeBirthdayPackage = useCallback((index: number) => {
    setState(prev => {
      const newPackages = [...prev.formData.birthdayPackages];
      if (index >= 0 && index < newPackages.length) {
        newPackages.splice(index, 1);
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          birthdayPackages: newPackages
        },
        isFormDirty: true
      };
    });
  }, []);

  const updateFoodAndBeverage = useCallback((foodAndBeverage: FoodAndBeverage) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        foodAndBeverage
      },
      isFormDirty: true
    }));
  }, []);

  const addMarketingChannel = useCallback((channel: MarketingChannel) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        marketingChannels: [...prev.formData.marketingChannels, channel]
      },
      isFormDirty: true
    }));
  }, []);

  const updateMarketingChannel = useCallback((index: number, channel: MarketingChannel) => {
    setState(prev => {
      const newChannels = [...prev.formData.marketingChannels];
      if (index >= 0 && index < newChannels.length) {
        newChannels[index] = channel;
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          marketingChannels: newChannels
        },
        isFormDirty: true
      };
    });
  }, []);

  const removeMarketingChannel = useCallback((index: number) => {
    setState(prev => {
      const newChannels = [...prev.formData.marketingChannels];
      if (index >= 0 && index < newChannels.length) {
        newChannels.splice(index, 1);
      }
      return {
        ...prev,
        formData: {
          ...prev.formData,
          marketingChannels: newChannels
        },
        isFormDirty: true
      };
    });
  }, []);

  // UI actions
  const setActiveTab = useCallback((tab: string) => {
    setState(prev => ({ ...prev, activeTab: tab }));
  }, []);

  const toggleSection = useCallback((section: string) => {
    setState(prev => ({
      ...prev,
      expandedSections: {
        ...prev.expandedSections,
        [section]: !prev.expandedSections[section]
      }
    }));
  }, []);

  // API actions
  const loadBusinessIntelligence = useCallback(async (projectId: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const bi = await businessIntelligenceService.getBusinessIntelligence(projectId);
      setState(prev => ({
        ...prev,
        businessIntelligence: bi,
        formData: bi ? convertToFormData(bi) : defaultFormData,
        isLoading: false,
        isFormDirty: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load business intelligence',
        isLoading: false
      }));
    }
  }, []);

  const saveBusinessIntelligence = useCallback(async (projectId: string) => {
    setState(prev => ({ ...prev, isSaving: true, saveError: null }));
    
    try {
      const businessProfile = convertFormDataToBusinessProfile(state.formData);
      
      let bi: BusinessIntelligence;
      if (state.businessIntelligence) {
        // Update existing
        bi = await businessIntelligenceService.updateBusinessIntelligence(projectId, {
          company_profile: businessProfile,
        });
      } else {
        // Create new
        bi = await businessIntelligenceService.createBusinessIntelligence(projectId, {
          industry: 'entertainment',
          company_profile: businessProfile,
        });
      }
      
      setState(prev => ({
        ...prev,
        businessIntelligence: bi,
        isSaving: false,
        isFormDirty: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        saveError: error instanceof Error ? error.message : 'Failed to save business intelligence',
        isSaving: false
      }));
    }
  }, [state.formData, state.businessIntelligence]);

  const discoverCompetitors = useCallback(async (projectId: string, location?: string, radiusMiles: number = 5.0, keyword?: string) => {
    setState(prev => ({
      ...prev,
      isDiscovering: true,
      discoveryError: null,
      discoveryResults: []
    }));
    
    try {
      const results = await businessIntelligenceService.discoverCompetitors(
        projectId,
        location,
        radiusMiles,
        keyword
      );
      
      setState(prev => ({
        ...prev,
        discoveryResults: results.competitors || [],
        isDiscovering: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        discoveryError: error instanceof Error ? error.message : 'Failed to discover competitors',
        isDiscovering: false
      }));
    }
  }, []);

  const fetchFromGoogle = useCallback(async (projectId: string, businessName: string, address?: string) => {
    try {
      const result = await businessIntelligenceService.fetchFromGoogle(projectId, businessName, address);
      return result;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch from Google'
      }));
      throw error;
    }
  }, []);

  const addCompetitorFromGoogle = useCallback(async (projectId: string, placeId: string, competitorName?: string, relationship: string = 'direct', notes?: string) => {
    try {
      const bi = await businessIntelligenceService.addCompetitorFromGoogle(
        projectId,
        placeId,
        competitorName,
        relationship,
        notes
      );
      
      setState(prev => ({
        ...prev,
        businessIntelligence: bi
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to add competitor'
      }));
      throw error;
    }
  }, []);

  return {
    // State
    ...state,
    
    // Actions
    setBusinessIntelligence,
    setLoading,
    setError,
    updateFormData,
    resetFormData,
    setFormDirty,
    updateCompanyProfile,
    addAttraction,
    updateAttraction,
    removeAttraction,
    addMembershipPlan,
    updateMembershipPlan,
    removeMembershipPlan,
    addBirthdayPackage,
    updateBirthdayPackage,
    removeBirthdayPackage,
    updateFoodAndBeverage,
    addMarketingChannel,
    updateMarketingChannel,
    removeMarketingChannel,
    setActiveTab,
    toggleSection,
    loadBusinessIntelligence,
    saveBusinessIntelligence,
    discoverCompetitors,
    fetchFromGoogle,
    addCompetitorFromGoogle,
  };
}

// Helper functions
function convertToFormData(bi: BusinessIntelligence): BusinessIntelligenceFormData {
  const companyProfile = bi.company_profile;
  
  return {
    companyProfile: {
      locationName: companyProfile.location_name,
      address: companyProfile.address,
      isExistingLocation: companyProfile.is_existing_location,
      openingDate: companyProfile.opening_date ? new Date(companyProfile.opening_date) : undefined,
      website: companyProfile.source_info.website,
      isFranchise: companyProfile.source_info.is_franchise,
      franchiseName: companyProfile.source_info.franchise_name,
    },
    attractions: companyProfile.attractions || [],
    membershipPlans: companyProfile.membership_plans || [],
    birthdayPackages: companyProfile.birthday_packages || [],
    foodAndBeverage: companyProfile.food_and_beverage,
    operationalRatings: {
      cleanliness: companyProfile.cleanliness,
      staffEngagement: companyProfile.staff_engagement,
      safetyRating: companyProfile.safety_rating,
    },
    onlinePresence: {
      googleRating: companyProfile.google_rating,
      totalReviews: companyProfile.total_reviews,
      reviewNotes: companyProfile.review_notes,
    },
    marketingChannels: companyProfile.marketing_channels || [],
  };
}

function convertFormDataToBusinessProfile(formData: BusinessIntelligenceFormData): BusinessProfile {
  return {
    location_name: formData.companyProfile.locationName,
    address: formData.companyProfile.address,
    is_existing_location: formData.companyProfile.isExistingLocation,
    opening_date: formData.companyProfile.openingDate?.toISOString(),
    source_info: {
      website: formData.companyProfile.website,
      is_franchise: formData.companyProfile.isFranchise,
      franchise_name: formData.companyProfile.franchiseName,
      has_fdd: false,
      verification_date: new Date().toISOString(),
      data_confidence: 3,
    },
    attractions: formData.attractions,
    membership_plans: formData.membershipPlans,
    birthday_packages: formData.birthdayPackages,
    food_and_beverage: formData.foodAndBeverage,
    cleanliness: formData.operationalRatings.cleanliness,
    staff_engagement: formData.operationalRatings.staffEngagement,
    safety_rating: formData.operationalRatings.safetyRating,
    marketing_channels: formData.marketingChannels,
    google_rating: formData.onlinePresence.googleRating,
    total_reviews: formData.onlinePresence.totalReviews,
    review_notes: formData.onlinePresence.reviewNotes,
    created_at: new Date().toISOString(),
  };
}
